import { Account } from '@adtrackify/at-tracking-event-types';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

import { CommandItem } from '@/components/ui/command';
import { useStableEventHandler } from '@/hooks/react/use-stable-event-handler';
import { cn } from '@/lib/utils';

interface AccountListItemProps {
  account: Account;
  isSelected: boolean;
  isLoading: boolean;
  onSelect: (accountId: string) => void;
}

export function AccountListItem({ account, isSelected, isLoading, onSelect }: AccountListItemProps) {
  const isInactive = account.subscription?.status !== 'active';
  const isStripeGateway = account.subscription?.paymentGateway !== 'shopify';
  const showWarning = isInactive && isStripeGateway;
  const initial = account.accountName?.charAt(0).toUpperCase() ?? 'W';
  const handleSelect = useStableEventHandler(() => onSelect(account.id));

  return (
    <CommandItem
      key={account.id}
      onSelect={handleSelect}
      className="flex w-full min-w-0 items-center gap-2 overflow-hidden px-2 py-1.5"
    >
      <div className="flex size-5 shrink-0 items-center justify-center rounded bg-surface-inset type-micro font-semibold">
        {initial}
      </div>
      <span
        className={cn(
          'block min-w-0 flex-1 truncate type-body',
          showWarning && 'text-content-tertiary'
        )}
        title={account.accountName}
      >
        {account.accountName}
      </span>
      {showWarning ? <AlertCircle className="size-3.5 shrink-0 text-destructive" /> : null}
      {isLoading && isSelected ? (
        <Loader2 className="size-3.5 shrink-0 animate-spin" />
      ) : (
        <Check
          className={cn(
            'size-3.5 shrink-0',
            isSelected ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
    </CommandItem>
  );
}
